import React, { useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import Modal from 'components/common/modal-component';
import { ModalButton } from 'models/utils/common/modal-button-model';
import image1 from 'icons/image1.png';
import image2 from 'icons/image2.png';
import image3 from 'icons/image3.png';
import image4 from 'icons/image4.png';
import image5 from 'icons/image5.png';

export const STATEMENT_RECENT_UPDATES_ID_BLOCK = 'statement-recent-update-id';
export const STATEMENT_RECENT_UPDATES_BLOCK = 'statement-recent-updates';

const IMAGE_CONFIG = [
  { id: 'image1', src: image1, alt: 'workpaper-report' },
  { id: 'image2', src: image2, alt: 'exception-report' },
  { id: 'image3', src: image3, alt: 'drag&drop-annotations' },
  { id: 'image4', src: image4, alt: 'Q&A-chatbot-manual-prompt' },
  { id: 'image5', src: image5, alt: 'Q&A-chatbot-prompt' },
];

let imageIdCounter = 0;
const generateId = () => `${Date.now()}-${++imageIdCounter}`;

const ImageItem = React.memo(({ config }) => {
  return (
    <div className={`${STATEMENT_RECENT_UPDATES_BLOCK}__${config.id}`}>
      <div className={`${STATEMENT_RECENT_UPDATES_BLOCK}__image-container`}>
        <img
          src={config.src}
          alt={config.alt}
          className={`${STATEMENT_RECENT_UPDATES_BLOCK}__image`}
          draggable={false}
        />
      </div>
    </div>
  );
});

ImageItem.displayName = 'ImageItem';

const RecentStatementUpdatesModal = ({ onClose, onSave }) => {
  // Preload images to prevent loading issues
  useEffect(() => {
    IMAGE_CONFIG.forEach((config) => {
      const img = new Image();
      img.src = config.src;
    });
  }, []);

  const renderImage = useCallback(
    (config) => <ImageItem key={config.id} config={config} />,
    [],
  );

  const renderedImages = useMemo(() => {
    return {
      image1: renderImage(IMAGE_CONFIG[0]),
      image2: renderImage(IMAGE_CONFIG[1]),
      image3: renderImage(IMAGE_CONFIG[2]),
      images4and5: (
        <div
          className={`${STATEMENT_RECENT_UPDATES_BLOCK}__images-row`}
          key="images4and5"
        >
          {renderImage(IMAGE_CONFIG[3])}
          {renderImage(IMAGE_CONFIG[4])}
        </div>
      ),
    };
  }, [renderImage]);

  const primaryButton = useMemo(
    () =>
      new ModalButton({
        text: 'recent-updates-information-modal.button',
        onClick: () => onSave(),
      }),
    [onSave],
  );

  const secondaryButton = useMemo(
    () =>
      new ModalButton({
        text: 'common.close',
        onClick: () => onClose(),
      }),
    [onClose],
  );

  const formattedMessageValues = useMemo(
    () => ({
      ol: (...chunks) => (
        <ol
          key={`${STATEMENT_RECENT_UPDATES_ID_BLOCK}-olTag-${generateId()}`}
          className={`${STATEMENT_RECENT_UPDATES_BLOCK}`}
        >
          {chunks}
        </ol>
      ),
      image1: renderedImages.image1,
      image2: renderedImages.image2,
      image3: renderedImages.image3,
      images4and5: renderedImages.images4and5,
      li: (...chunks) => (
        <li key={`${STATEMENT_RECENT_UPDATES_ID_BLOCK}-liTag-${generateId()}`}>
          {chunks}
        </li>
      ),
      b: (...chunks) => (
        <b key={`${STATEMENT_RECENT_UPDATES_ID_BLOCK}-bTag-${generateId()}`}>
          {chunks}
        </b>
      ),
      br: <br key={`${STATEMENT_RECENT_UPDATES_ID_BLOCK}__brTag`} />,
    }),
    [renderedImages],
  );

  return (
    <Modal
      id={STATEMENT_RECENT_UPDATES_ID_BLOCK}
      onClose={onClose}
      title={'statement-recent-updates-information-modal.title'}
      className={`${STATEMENT_RECENT_UPDATES_BLOCK}__modal`}
      primaryModalButton={primaryButton}
      secondaryModalButton={secondaryButton}
    >
      <div className={`${STATEMENT_RECENT_UPDATES_BLOCK}__container`}>
        <div
          className={`${STATEMENT_RECENT_UPDATES_BLOCK}__recent-updates-message`}
        >
          <FormattedMessage
            id={'statement-recent-updates-information-modal.message'}
            values={formattedMessageValues}
          />
        </div>
      </div>
    </Modal>
  );
};

RecentStatementUpdatesModal.propTypes = {
  /** Modal panel onClose function  */
  onClose: PropTypes.func.isRequired,
  /** Modal panel onSave function  */
  onSave: PropTypes.func.isRequired,
};

export default RecentStatementUpdatesModal;
